import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const TasksContext = createContext(null);

export const useTasks = () => {
  const context = useContext(TasksContext);
  if (!context) throw new Error('useTasks must be used within TasksProvider');
  return context;
};

export const TasksProvider = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch tasks from MongoDB backend
  const fetchTasks = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/tasks');
      setTasks(response.data || []);
    } catch (err) {
      console.log('Tasks: Error fetching', err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Create task (admin only)
  const createTask = async (taskData) => {
    if (!user) return;

    try {
      const response = await api.post('/tasks', {
        title: taskData.title,
        description: taskData.description || '',
        priority: taskData.priority || 'normal',
        due_date: taskData.due_date || null,
        assigned_to: taskData.assigned_to || null
      });
      
      if (response.data) {
        setTasks(prev => [response.data, ...prev]);
      }
    } catch (err) {
      console.error('Create task error:', err);
      throw err;
    }
  };

  // Update task status
  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.patch(`/tasks/${taskId}`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      console.error('Update task error:', err);
      throw err;
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (err) {
      console.error('Delete task error:', err);
      throw err;
    }
  };

  // Fetch on mount
  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [user, fetchTasks]);

  // Get tasks by priority
  const getTasksByPriority = (priority) => tasks.filter(t => t.priority === priority && t.status !== 'done');
  const getOpenTasks = () => tasks.filter(t => t.status === 'open');
  const getDoneTasks = () => tasks.filter(t => t.status === 'done');

  return (
    <TasksContext.Provider value={{
      tasks,
      loading,
      createTask,
      updateTaskStatus,
      deleteTask,
      fetchTasks,
      getTasksByPriority,
      getOpenTasks,
      getDoneTasks,
      isAdmin
    }}>
      {children}
    </TasksContext.Provider>
  );
};

export default TasksContext;
